import { useMemo } from "react";
import type { RefObject } from "react";
import { useIntl } from "react-intl";

import { CollapsibleCard } from "@/components/CollapsibleCard/CollapsibleCard";
import { getCategoryIcon } from "@/features/hotels/components/hotel/facilityIcons";
import type { HotelData } from "@/features/hotels/types/types";
import {
  buildHotelFacilitiesDisplayModel,
  getLocalizedCategoryLabel,
  getLocalizedFacilityLabel,
  type HotelFacilitiesDisplayModel,
} from "@/features/hotels/utils/facilities/buildHotelFacilitiesDisplayModel";

type Props = {
  hotel: HotelData;
  sectionRef?: RefObject<HTMLElement | null>;
  facilitiesModel?: HotelFacilitiesDisplayModel;
};

export const HotelFacilities = ({
  hotel,
  sectionRef,
  facilitiesModel,
}: Props) => {
  const intl = useIntl();

  const { groups } = useMemo(
    () =>
      facilitiesModel ??
      buildHotelFacilitiesDisplayModel(hotel.descriptions?.facilities),
    [hotel.descriptions?.facilities, facilitiesModel],
  );

  if (groups.length === 0) {
    return null;
  }

  return (
    <CollapsibleCard
      ref={sectionRef}
      title={
        <span className="text-lg font-semibold text-text-primary">
          {intl.formatMessage({
            defaultMessage: "Hotel Facilities",
            description: "Section heading for full hotel facilities list",
          })}
        </span>
      }
      expandedByDefault
      collapsible
      backgroundColor="white"
    >
      <ul className="flex list-none flex-col gap-4">
        {groups.map((group) => {
          const CategoryIcon = getCategoryIcon(group.key);

          return (
            <li key={group.key} className="flex flex-col gap-3">
              <div className="flex items-center gap-1">
                <CategoryIcon className="h-6 w-6 text-text-primary" />
                <h4 className="font-semibold text-text-primary">
                  {getLocalizedCategoryLabel(intl, group.key, group.label)}
                </h4>
              </div>

              <ul className="flex flex-col gap-2 ps-2.5">
                {group.items.map((facility) => (
                  <li
                    key={facility.key}
                    className="flex items-center gap-2 text-sm text-text-secondary"
                  >
                    <span aria-hidden="true">•</span>
                    <span>
                      {getLocalizedFacilityLabel(
                        intl,
                        facility.localizationKey,
                        facility.label,
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </li>
          );
        })}
      </ul>
    </CollapsibleCard>
  );
};
